import { AspectRatioValue } from '@/components/AspectRatioDropdown';
import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import ArrowLeft from '@/images/arrows/ArrowLeft.svg';
import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { fetchTemplateRawData, parsePageData } from '@/pages/AdvancedEditor/components/panels/templates/hooks';
import { getPageProperty, PolotnoStoreActivePageKeys } from '@/pages/AdvancedEditor/hooks';
import { SIDEBAR_PANELS } from '@/pages/AdvancedEditor/types';
import { ContentBar } from '@/pages/home/components/Content/ContentBar';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { useTemplateGalleryModal } from '@/state/template-gallery/useTemplateGalleryModal';
import theme from '@/theme';
import { IProjectTemplate } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogTitle, IconButton } from '@mui/material';
import { StoreType } from 'polotno/model/store';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SectionContainer } from './SectionContainer';
import { TemplateGalleryPreviewSkeleton, TemplateGalleryScenesSkeleton } from './TemplateGalleryPreviewSkeleton';

const Container = styled.div`
    height: 100%;
    width: 100%;
    position: absolute;
    background: ${({ theme }) => theme.colors.black[50]};
    z-index: 2;
`;

const StyledVideo = styled.video`
    height: 100%;
    border-radius: 12px;
    ::cue {
        text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px 1px 0 #000,
            1px 1px 0 #000;
    }
`;

const VideoContainer = styled.div`
    height: calc(100% - 170px - 48px);
    width: 100%;
    display: flex;
    justify-content: center;

    border-radius: 14px;
    padding: 24px;
    background-color: ${theme.colors.black[100]};
    box-sizing: border-box;
`;

const StyledSectionContainer = styled(SectionContainer)`
    height: calc(100% - 88px);
`;

const SceneTile = styled.img<{ isSelected: boolean }>`
    height: 100px;
    border: 2px solid ${props => (props.isSelected ? theme.colors.primary.main : theme.colors.black[300])};
    border-radius: 8px;
    cursor: pointer;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: end;
    gap: 16px;
    padding: 0px 24px;
`;

const StyledContentBar = styled(ContentBar)<{ hidden: boolean }>`
    padding: 0px 24px;
    overflow: hidden;
    display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
`;

const getPageDurationInSeconds = (duration: number) => {
    return duration / 1000;
};

const StyledMaskedIcon = styled(MaskedIcon)`
    color: ${theme.colors.black[700]};
`;

const Header = styled.div`
    position: relative;
    display: flex;
    align-items: start;
    justify-content: center;
    gap: 16px;
    padding: 24px;
`;

const StyledBackButton = styled(IconButton)`
    padding: 0;
    border-radius: 12px;
    height: 40px;
`;

const ModalTitle = styled(DialogTitle)`
    text-align: center;
    font-weight: bold;
    padding: 0.5rem 0 0 !important;
`;

const CloseButton = styled(IconButton)`
    margin-left: auto;
`;

interface Props {
    selectedTemplate: IProjectTemplate;
    selectedTemplateIndex: number;
    onBack: () => void;
    from?: 'Home' | 'Videos';
    aspectRatio: AspectRatioValue;
    search: string;
    filter: string;
}

export const TemplateGalleryPreview = ({ selectedTemplate, selectedTemplateIndex, onBack, from, aspectRatio, search, filter }: Props) => {
    const mixpanel = useAnalytics();
    const { navigateToAdvancedEditor, isLoading } = useNavigateToAdvancedEditor();
    const { close } = useTemplateGalleryModal();
    const [storeIsLoading, setStoreIsLoading] = useState(true);
    const brandKitEnabled = useFlag('projects_brandkit_enabled', false);

    const [selectedScene, setSelectedScene] = useState<string | null>(null);
    const [templateData, setTemplateData] = useState<StoreType | null>(null);
    const [stopTimestamp, setStopTimestamp] = useState<number | null>(null);
    const [clickedButton, setClickedButton] = useState<'multiple' | 'single' | null>(null);
    const [isImagesLoaded, setIsImagesLoaded] = useState(false);
    const isProgrammaticChange = useRef(false);

    const videoRef = useRef<HTMLVideoElement>(null);
    const sectionContainerRef = useRef<HTMLDivElement>(null);

    const pagesWithTimestamps: (Page & { startTime: number; endTime: number })[] = useMemo(() => {
        let accumulatedDuration = 0;

        return (templateData?.pages || []).map((page, index) => {
            const pageWithStartTime = {
                ...page,
                startTime: index === 0 ? 0 : accumulatedDuration + 0.1, // adding 0.1 to avoid displaying previous scene last frame
                endTime: accumulatedDuration + getPageDurationInSeconds(page.duration),
            };

            accumulatedDuration += getPageDurationInSeconds(page.duration);

            return pageWithStartTime;
        });
    }, [templateData]);

    useEffect(() => {
        setStoreIsLoading(true);
        fetchTemplateRawData(selectedTemplate)
            .then(setTemplateData)
            .then(() => setStoreIsLoading(false));

        mixpanel?.track('template-preview', {
            event: 'view',
            componentType: 'modal',
            templateName: selectedTemplate.name,
            templateId: selectedTemplate.id,
            aspectRatio,
            useCaseFilter: [filter],
            textSearch: search,
            index: selectedTemplateIndex,
        });
    }, [selectedTemplate]);

    useEffect(() => {
        if (storeIsLoading) return;

        setIsImagesLoaded(false);

        const thumbnails = pagesWithTimestamps.map(page => getPageProperty(page, PolotnoStoreActivePageKeys.thumbnail));

        if (!thumbnails.length) return;

        const thumnailsPromises = thumbnails.map(
            th =>
                new Promise(resolve => {
                    const img = new Image();
                    img.src = th;
                    img.onload = () => resolve(img);
                })
        );

        Promise.all(thumnailsPromises).then(() => {
            setIsImagesLoaded(true);
        });
    }, [storeIsLoading]);

    useEffect(() => {
        const videoElement = videoRef.current;
        let animationFrameId: number;

        const onPlayActions = () => {
            updateSelectedScene();
            handleStop();
            animationFrameId = requestAnimationFrame(onPlayActions);
        };

        const handleStop = () => {
            if (!videoElement) return;

            // 0.1 is a buffer to avoid selecting next scene
            if (stopTimestamp && videoElement.currentTime + 0.1 >= stopTimestamp) {
                videoElement.pause();
                handleStopTimestampReset();
            }
        };

        const updateSelectedScene = () => {
            if (!videoElement || !templateData) return;

            const currentTime = videoElement.currentTime;

            const scene = pagesWithTimestamps.find(
                page => currentTime >= page.startTime && currentTime < page.startTime + getPageDurationInSeconds(page.duration)
            );

            if (!scene || scene.id === selectedScene) return;

            setSelectedScene(scene.id);
            const element = sectionContainerRef.current?.querySelector(`#page-tile-${scene.id}`);

            if (!element) return;
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        };

        animationFrameId = requestAnimationFrame(onPlayActions);

        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [videoRef, templateData, pagesWithTimestamps, stopTimestamp, selectedScene]);

    const handleSeeked = () => {
        if (isProgrammaticChange.current) {
            isProgrammaticChange.current = false;
            return;
        }

        handleStopTimestampReset();
    };

    const handleStopTimestampReset = () => {
        setStopTimestamp(null);
    };

    const setTimestampProgrammatically = (timestamp: number) => {
        if (videoRef.current) {
            videoRef.current.currentTime = timestamp;
            isProgrammaticChange.current = true;
        }
    };

    const handleSceneClick = (page: Page) => {
        if (videoRef.current) {
            setTimestampProgrammatically(page.startTime);
            setStopTimestamp(page.startTime + getPageDurationInSeconds(page.duration));
            videoRef.current.play();
        }
    };

    const reportTemplateClick = (action: 'add-all-scenes' | 'add-selected-scene') => {
        mixpanel?.track('templates-preview', {
            event: 'click',
            componentType: 'modal',
            action,
            templateName: selectedTemplate.name,
            templateId: selectedTemplate.id,
            aspectRatio,
            useCaseFilter: [filter],
            textSearch: search,
            index: selectedTemplateIndex,
        });
    };

    const handleAddAllScenes = async () => {
        if (!templateData) return;

        setClickedButton('multiple');
        const pages = (templateData.pages || []).map(page => parsePageData(templateData, page, brandKitEnabled));
        const parsedStore = { ...templateData, pages } as StoreType;

        await navigateToAdvancedEditor({
            options: {
                template: parsedStore,
                from,
                initPanel: SIDEBAR_PANELS.TEMPLATES,
                selectedTemplate,
                selectedTemplateIndex,
                aspectRatio,
            },
            mixPanelFrom: 'template-all-scenes',
        });
        reportTemplateClick('add-all-scenes');
        close();
    };

    const handleAddSelectedScene = async () => {
        if (!templateData || !selectedScene) return;

        setClickedButton('single');
        const pages = (templateData.pages || [])
            .filter(page => page.id === selectedScene)
            .map(page => parsePageData(templateData, page, brandKitEnabled));
        const parsedStore = { ...templateData, pages } as StoreType;

        await navigateToAdvancedEditor({
            options: {
                template: parsedStore,
                from,
                initPanel: SIDEBAR_PANELS.TEMPLATES,
                selectedTemplate,
                selectedTemplateIndex,
                aspectRatio,
            },
            mixPanelFrom: 'template-single-scene',
        });
        reportTemplateClick('add-selected-scene');
        close();
    };

    return (
        <Container>
            <Header>
                <StyledBackButton onClick={onBack}>
                    <StyledMaskedIcon src={ArrowLeft} size="32px" />
                </StyledBackButton>
                <ModalTitle>{selectedTemplate.name}</ModalTitle>
                <CloseButton onClick={close}>
                    <CloseIcon color="secondary" fontSize="large" />
                </CloseButton>
            </Header>

            <StyledSectionContainer ref={sectionContainerRef}>
                {!storeIsLoading && (
                    <>
                        <VideoContainer>
                            <StyledVideo
                                ref={videoRef}
                                poster={selectedTemplate.scenesThumbnailsUrls[0]}
                                controlsList="nodownload"
                                controls={true}
                                playsInline
                                autoPlay
                                preload="auto"
                                onSeeked={handleSeeked}>
                                <source src={selectedTemplate.videoUrl || selectedTemplate.previewUrl} type="video/mp4" />
                                Your browser does not support the video tag.
                            </StyledVideo>
                        </VideoContainer>

                        {!isImagesLoaded && <TemplateGalleryScenesSkeleton cropMode={selectedTemplate.cropMode} />}
                        <StyledContentBar id="template-preview-content-bar" scrollButtonPadding={16} hidden={!isImagesLoaded}>
                            {pagesWithTimestamps.map(page => (
                                <SceneTile
                                    key={page.id}
                                    id={`page-tile-${page.id}`}
                                    src={getPageProperty(page, PolotnoStoreActivePageKeys.thumbnail)}
                                    isSelected={selectedScene === page.id}
                                    onClick={() => handleSceneClick(page)}
                                />
                            ))}
                        </StyledContentBar>
                    </>
                )}

                {storeIsLoading && <TemplateGalleryPreviewSkeleton cropMode={selectedTemplate.cropMode} />}

                <ButtonContainer>
                    <Button
                        data-testid="template-preview-add-selected-scene-button"
                        disabled={!selectedScene || storeIsLoading}
                        variant="outlined"
                        color="secondary"
                        onClick={handleAddSelectedScene}
                        isLoading={clickedButton === 'single' && isLoading}
                        loadingText="Add selected scene">
                        Add selected scene
                    </Button>

                    <Button
                        data-testid="template-preview-add-all-scenes-button"
                        disabled={storeIsLoading}
                        variant="contained"
                        color="primary"
                        onClick={handleAddAllScenes}
                        isLoading={clickedButton === 'multiple' && isLoading}
                        loadingText="Add all scenes">
                        Add all scenes
                    </Button>
                </ButtonContainer>
            </StyledSectionContainer>
        </Container>
    );
};
